import React, { useState, useEffect } from 'react';
import { Box, Typography, Stack, IconButton, ToggleButton, ToggleButtonGroup } from '@mui/material';
import { Add, DeleteOutline } from '@mui/icons-material';
import { FormControlsForFields } from '../../../schemed';
import { SimpleDialog, FormGrid } from '../../../primitives';
import { ContactsBatchUpdateData } from '../../data/useContactsBatchUpdate';
import { ContactSchema, CRMAttributeConfig, CustomAttributeType } from '../../types';
import { ContactTags } from '../ContactTags';
import { SmallAddButton } from '../Primitives';
import { AutoSuggestMultiple } from '../../../primitives/AutoSuggests';
import { CRMGeoSuggestionsKeys, useEnsureGeoLoaded } from '../../CRMGeoContext';
import { useContactAttributesConfig } from '../../data/useContactAttributesConfig';
import { FieldType, Schema } from '../../../../hooks/useSchema';
import { CustomAttributeControl } from './CustomAttributes/CustomAttributesEditors';

export interface ContactsBatchUpdateDialogProps {
  data: ContactsBatchUpdateData;
  schema: Schema;
}

// Allow string to handle dynamically generated 'attr_...' keys
type BatchOpType = 
  | 'add_tags' | 'remove_tags'
  | 'add_categories' | 'remove_categories'
  | 'add_geo_country' | 'remove_geo_country'
  | 'add_geo_city' | 'remove_geo_city'
  | 'owner_id' | 'do_not_contact' 
  | 'consent_pd' | 'consent_emails' | 'communication_language'
  | string;

interface OpItem {
  key: string;
  label: any;
  isAttr?: boolean;
  isChange?: boolean;
  attr?: CRMAttributeConfig;
}

const BATCH_OPS: OpItem[] = [
  { key: 'add_tags', label: 'Добавить теги' },
  { key: 'remove_tags', label: 'Удалить теги' },
  { key: 'add_categories', label: 'Добавить категории' },
  { key: 'remove_categories', label: 'Удалить категории' },
  { key: 'add_geo_country', label: 'Добавить страны' },
  { key: 'remove_geo_country', label: 'Удалить страны' },
  { key: 'add_geo_city', label: 'Добавить города' },
  { key: 'remove_geo_city', label: 'Удалить города' },
  { key: 'do_not_contact', label: 'Запрет связи', isChange: true },
  { key: 'consent_pd', label: 'Согласие ПД', isChange: true },
  { key: 'consent_emails', label: 'Согласие на рассылки', isChange: true },
  { key: 'communication_language', label: 'Язык коммуникации', isChange: true },
];

export const ContactsBatchUpdateDialog = ({ data, schema }: ContactsBatchUpdateDialogProps) => {
  const [activeOps, setActiveOps] = useState<Set<BatchOpType>>(new Set());
  
  // Pull dynamic attributes from configuration
  const attributesConfig = useContactAttributesConfig();
  const customAttributes = attributesConfig.attributes || [];

  useEffect(() => {
    if (!data.isOpen) {
      setActiveOps(new Set());
    }
  }, [data.isOpen]);

  useEnsureGeoLoaded(
    activeOps.has("add_geo_country") || 
    activeOps.has("remove_geo_country") || 
    activeOps.has("add_geo_city") || 
    activeOps.has("remove_geo_city")
  );

  const addOp = (opKey: BatchOpType) => {
    setActiveOps(prev => new Set(prev).add(opKey));
  };

  const removeOp = (opKey: BatchOpType) => {
    setActiveOps(prev => {
      const next = new Set(prev);
      next.delete(opKey);
      return next;
    });

    if (opKey.startsWith('attr_')) {
      // Clear custom attribute from payload
      const attrCode = opKey.replace('attr_', '');
      const newAttrs = (data.item?.attributes || []).filter(a => a.attribute !== attrCode);
      data.update({ attributes: newAttrs });
    } else {
      // Clear standard field from payload
      const opDef = BATCH_OPS.find(o => o.key === opKey);
      if (opDef?.isChange) {
        const newChanges = { ...(data.item?.changes || {}) };
        delete (newChanges as any)[opKey as string];
        data.update({ changes: newChanges });
      } else {
        data.update({ [opKey]: [] });
      }
    }
  };

  // Central handler to manage attribute payload changes
  const handleAttrChange = (code: string, operation: 'set' | 'append', value: any, type?: string) => {
    const attrs = [...(data.item?.attributes || [])];
    const idx = attrs.findIndex(a => a.attribute === code);
    
    if (idx >= 0) {
      attrs[idx] = { attribute: code, operation, value, type };
    } else {
      attrs.push({ attribute: code, operation, value, type });
    }
    data.update({ attributes: attrs });
  };

  const activeOpKeys = Array.from(activeOps);
  
  // Build inactive ops, injecting available unselected custom attributes
  const inactiveOps: OpItem[] = [
    ...BATCH_OPS.filter(op => !activeOps.has(op.key)),
    ...customAttributes
      .filter(attr => !activeOps.has(`attr_${attr.code}`))
      .map(attr => ({ 
        key: `attr_${attr.code}`, 
        label: attr.label || attr.code,
        isAttr: true, 
        attr 
      }))
  ];

  return (
    <SimpleDialog
      {...data.dialogProps}
      dialogTitle={`Массовое обновление (${data.targetIds.length})`}
      save={data.save}
      maxWidth="sm"
      noFullscreen
      submitOnModEnter
    >
      <Stack spacing={3} mt={1}>
        {activeOpKeys.length > 0 && (
          <Stack spacing={1.5}>
            {activeOpKeys.map(opKey => {
              const isAttrOp = opKey.startsWith('attr_');
              
              let opDef: OpItem;
              if (isAttrOp) {
                const attrCode = opKey.replace('attr_', '');
                const attr = customAttributes.find(a => a.code === attrCode);
                opDef = {
                  key: opKey,
                  label: attr?.label || attr?.code || attrCode,
                  isAttr: true,
                  attr
                };
              } else {
                opDef = BATCH_OPS.find(o => o.key === opKey)!;
              }
              
              return (
                <Box 
                  key={opKey} 
                  sx={{ 
                    p: 1.5, 
                    border: '1px solid', 
                    borderColor: 'divider', 
                    borderRadius: 1,
                    bgcolor: 'background.paper',
                    position: 'relative'
                  }}
                >
                  <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                    <Typography variant="subtitle2" color="text.secondary">
                      {opDef.label}
                    </Typography>
                    <IconButton size="small" onClick={() => removeOp(opKey)} sx={{ m: -0.5 }}>
                      <DeleteOutline fontSize="small" />
                    </IconButton>
                  </Box>

                  {/* Render Custom Attributes using mock EditRecord */}
                  {isAttrOp && (() => {
                    const attr = opDef.attr;
                    if (!attr) return null;

                    const type = attr.type as string;
                    const canAppend = !type || type === FieldType.text || type === FieldType.textlong || type === CustomAttributeType.suggest_multi;

                    const attrState = data.item?.attributes?.find(a => a.attribute === attr.code) || { 
                      operation: canAppend ? 'append' : 'set', 
                      value: undefined 
                    };

                    return (
                      <Box display="flex" flexDirection="column" gap={1.5}>
                        {canAppend && (
                          <ToggleButtonGroup
                            size="small"
                            color="primary"
                            value={attrState.operation}
                            exclusive
                            onChange={(e, newOp) => {
                              if (newOp) handleAttrChange(attr.code, newOp, attrState.value, type);
                            }}
                          >
                            <ToggleButton value="set" sx={{ py: 0.5, px: 2 }}>Заменить</ToggleButton>
                            <ToggleButton value="append" sx={{ py: 0.5, px: 2 }}>Добавить</ToggleButton>
                          </ToggleButtonGroup>
                        )}
                        
                        <CustomAttributeControl
                          attribute={attr}
                          attributesConfig={attributesConfig}
                          editRecord={{
                            item: { _id: 'batch_update', attributes: { [attr.code]: attrState.value } } as any,
                            update: (changes) => {
                              const newVal = changes.attributes?.[attr.code];
                              handleAttrChange(attr.code, attrState.operation as 'set'|'append', newVal, type);
                            }
                          }}
                        />
                      </Box>
                    );
                  })()}

                  {/* Render Scalar Fields */}
                  {!isAttrOp && opDef.isChange && (
                    <FormGrid columns="1fr" noMargin>
                      <FormControlsForFields
                        data={data.item?.changes || {}}
                        onChange={(o, c) => data.update({ changes: { ...data.item?.changes, ...c } })}
                        schema={ContactSchema}
                        fields={[[opKey, { controlProps: { autoFocus: true } }]]}
                      />
                    </FormGrid>
                  )}

                  {/* Render Tags */}
                  {(opKey === 'add_tags' || opKey === 'remove_tags') && (
                    <ContactTags 
                      tagIds={(data.item as any)?.[opKey] || []} 
                      onUpdateTags={tags => data.update({ [opKey]: tags as number[] })} 
                    />
                  )}

                  {/* Render Categories */}
                  {(opKey === 'add_categories' || opKey === 'remove_categories') && (
                    <FormGrid columns="1fr" noMargin>
                      <FormControlsForFields
                        data={{ categories: (data.item as any)?.[opKey] || [] }}
                        onChange={(o, c) => data.update({ [opKey]: c.categories || [] })}
                        schema={schema}
                        fields={[["categories", { label: "", autoComplete: true, controlProps: { autoFocus: true } }]]}
                      />
                    </FormGrid>
                  )}

                  {/* Render Geo Fields */}
                  {(opKey === 'add_geo_country' || opKey === 'remove_geo_country') && (
                    <FormGrid columns="1fr" noMargin>
                      <FormControlsForFields
                        data={{ geo_country: data.item?.[opKey] || [] }}
                        onChange={(o, c) => data.update({ [opKey]: c.geo_country })}
                        schema={ContactSchema}
                        fields={[["geo_country", { control: AutoSuggestMultiple, suggestionsKey: CRMGeoSuggestionsKeys.countries, label: "", controlProps: { autoFocus: true } }]]}
                      />
                    </FormGrid>
                  )}

                  {(opKey === 'add_geo_city' || opKey === 'remove_geo_city') && (
                    <FormGrid columns="1fr" noMargin>
                      <FormControlsForFields
                        data={{ geo_city: data.item?.[opKey] || [] }}
                        onChange={(o, c) => data.update({ [opKey]: c.geo_city })}
                        schema={ContactSchema}
                        fields={[["geo_city", { control: AutoSuggestMultiple, suggestionsKey: CRMGeoSuggestionsKeys.cities, label: "", controlProps: { autoFocus: true } }]]}
                      />
                    </FormGrid>
                  )}
                </Box>
              );
            })}
          </Stack>
        )}

        {/* INACTIVE OPERATIONS SELECTION */}
        <Box>
          <Typography variant="body2" color="text.secondary" mb={1.5}>
            {activeOpKeys.length === 0 ? "Выберите данные для изменения:" : "Дополнительные действия:"}
          </Typography>
          <Box display="flex" flexWrap="wrap" gap={1}>
            {inactiveOps.map(op => (
              <SmallAddButton 
                key={op.key} 
                iconOnly={false} 
                icon={<Add />} 
                onClick={() => addOp(op.key)}
              >
                {op.label}
              </SmallAddButton>
            ))}
          </Box>
        </Box>
      </Stack>
    </SimpleDialog>
  );
};